import { useEffect, useState, useRef } from 'react';
import { RefreshCw, X } from 'lucide-react';

/**
 * AppUpdateBanner
 *
 * Polls `/api/version` every minute. When the backend reports a different
 * `build_id` than the one we captured on first load, we show a sticky banner
 * inviting the user to refresh. Clicking the button hard-reloads the tab
 * (bypassing any cached HTML/JS), which is the simplest way to make sure
 * every deployed user picks up the latest frontend bundle without losing
 * unsaved work — they choose when to refresh.
 *
 * No-op when REACT_APP_BACKEND_URL is missing or when the endpoint errors —
 * we never want this banner to break the app.
 */
const API = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 60_000; // 1 minute
const STORAGE_KEY = 'resawork-build-id';

export const AppUpdateBanner = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const initialBuildIdRef = useRef(null);

  useEffect(() => {
    if (!API) return;
    let cancelled = false;

    const fetchVersion = async () => {
      try {
        // `cache: no-store` is mandatory: without it, browsers (Safari in
        // particular) happily serve a 304 from the deploy that introduced
        // this very banner — defeating the whole purpose.
        const res = await fetch(`${API}/api/version`, { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        const id = data?.build_id;
        if (!id || cancelled) return;
        if (!initialBuildIdRef.current) {
          initialBuildIdRef.current = id;
          try { sessionStorage.setItem(STORAGE_KEY, id); } catch (e) {}
          return;
        }
        if (id !== initialBuildIdRef.current) {
          setUpdateAvailable(true);
        }
      } catch (e) {
        // Network/CORS errors are silently ignored — the banner is best-effort.
      }
    };

    fetchVersion();
    const intervalId = setInterval(fetchVersion, POLL_INTERVAL_MS);
    // Refresh check when the user comes back to the tab (typical: closed the
    // laptop overnight, opens it in the morning, the deploy happened at 6am).
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchVersion();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  if (!updateAvailable || dismissed) return null;

  const handleReload = () => {
    // Bust any cached HTML / SW state then hard reload.
    try {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations()
          .then((regs) => regs.forEach((r) => r.unregister()))
          .catch(() => {});
      }
      if (typeof caches !== 'undefined') {
        caches.keys()
          .then((names) => Promise.all(names.map((n) => caches.delete(n))))
          .catch(() => {});
      }
    } catch (e) {}
    // Append a cache-buster so even aggressive intermediaries serve fresh content.
    const sep = window.location.href.includes('?') ? '&' : '?';
    window.location.href = `${window.location.href}${sep}_v=${Date.now()}`;
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 right-4 z-[9999] flex items-center gap-3 rounded-lg bg-emerald-600 text-white shadow-xl px-4 py-3 text-sm max-w-sm"
      data-testid="app-update-banner"
    >
      <RefreshCw className="w-5 h-5 flex-shrink-0 animate-spin-slow" />
      <div className="flex-1">
        <div className="font-semibold">Nouvelle version disponible</div>
        <div className="text-emerald-50 text-xs">Cliquez pour recharger et profiter des dernières améliorations.</div>
      </div>
      <button
        onClick={handleReload}
        className="bg-white text-emerald-700 font-medium px-3 py-1.5 rounded hover:bg-emerald-50 transition-colors text-xs"
        data-testid="app-update-reload-button"
      >
        Recharger
      </button>
      <button
        onClick={() => setDismissed(true)}
        className="text-emerald-100 hover:text-white"
        aria-label="Ignorer"
        data-testid="app-update-dismiss-button"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default AppUpdateBanner;
